import { type LocationTypeEnum, type ShowroomFeaturedBrandsQuery, type ShowroomsQuery } from '../../__generated__/graphql-client-types';
import { SHOWROOM_LOCATION_LINK } from '../../constants/links';
import {
	PLUMBING_LOCATION_BASE_URL,
	READABLE_LOCATION_TYPES,
	SHORT_TO_FULL_DAY_NAME,
	SUPPORTED_LOCATION_TYPES
} from '../../constants/showroom';
import { type ShowroomAddress, type ShowroomBookApptEvent, ShowroomBookApptEventTypes } from '../../types/showroom.types';
import { generatePath } from '../../utils/routing/route.utils';

type DisplayAddressParams = {
	city?: string;
	state?: string;
	zipCode?: string;
};
export const displayAddress = ({ city = '', state = '', zipCode = '' }: DisplayAddressParams): string => {
	let address = '';
	address += city && `${city}, `;
	address += state && `${state} `;
	address += zipCode && `${zipCode}`;

	return address.trim();
};

type DisplayHoursParams = {
	startTime?: string;
	endTime?: string;
};

export const displayHours = (hours?: DisplayHoursParams | null): string => {
	if (!hours) {
		return 'Closed';
	}

	const { startTime, endTime } = hours;
	if (!startTime || !endTime) {
		return 'Closed';
	}

	return `${startTime}–${endTime}`;
};

export const isSupportedLocationType = (locationType: LocationTypeEnum) => {
	return SUPPORTED_LOCATION_TYPES.indexOf(locationType) > -1;
};

export const getHumanReadableLocationType = (locationType: LocationTypeEnum): string | undefined => {
	return READABLE_LOCATION_TYPES[locationType];
};

export const getSupportedLocationType = (locationTypes: LocationTypeEnum[]): LocationTypeEnum | undefined => {
	for (const locationType of SUPPORTED_LOCATION_TYPES) {
		if (locationTypes.includes(locationType)) {
			return locationType;
		}
	}
	return undefined;
};

export const createInfoBoxString = (locationName: string | undefined, url: string, address: ShowroomAddress): string => {
	const { addressLine1, addressLine2, city, state, zipCode, phoneNumber } = address;
	const locationInfoBoxName = (locationName && ` - ${locationName}`) || '';
	const title = `${city}, ${state}${locationInfoBoxName}`;
	let infoBoxString = `<div class="pa2" data-testid="info-box-string">`;
	infoBoxString += url ? `<a href="${url}" class="link" data-testid="showroom-link">${title}</a>` : `<p>${title}</p>`;
	infoBoxString += `<p class="ma0 mt1">${addressLine1}</p>`;
	infoBoxString += addressLine2 ? `<p class="ma0">${addressLine2}</p>` : '';
	infoBoxString += `<p class="ma0">${city}, ${state} ${zipCode}</p>`;
	infoBoxString += phoneNumber ? `<p class="ma0"><span class="font-medium">Phone</span>: ${phoneNumber}</p>` : '';
	infoBoxString += `</div>`;
	return infoBoxString;
};

/**
 * Convert a 3-letter day of the week to the full word (lookup is case-insensitive, and a lowercase value is returned).
 */
export const getFullDayName = (shortDayName: string): string | undefined => {
	return SHORT_TO_FULL_DAY_NAME[shortDayName.toLowerCase()];
};

/**
 * Make a URL for the plumbing location, which must have this format:
 *      `https://www.ferguson.com/store/${stateSlug}/${citySlug}/plumbingpvf-${costCenterId}`
 * Example:
 *      FergusonHome URL for a Showroom at the same address, sharing the same costCenterId: https://www.fergusonhome.com/showroom/branch/eugene-or-showroom-3004
 *      Plumbing URL at the same address, sharing the same costCenterId: https://www.ferguson.com/store/or/eugene/plumbingpvf-3004
 */
export const makePlumbingLocationUrl = (city: string | undefined, state: string | undefined, costCenterId: string | undefined) => {
	const citySlug = city?.toLowerCase().trim().replace(/\s+/g, '+');
	const stateSlug = state?.toLowerCase();
	return `${PLUMBING_LOCATION_BASE_URL}/store/${stateSlug}/${citySlug}/plumbingpvf-${costCenterId}`;
};

export const makeShowroomLocationLink = (branchPageUrl: string) => branchPageUrl && generatePath(SHOWROOM_LOCATION_LINK, { branchPageUrl });

export const isShowroomBookApptEvent = (event: any): event is ShowroomBookApptEvent => ShowroomBookApptEventTypes.includes(event?.type);

type ShowroomFeaturedBrandsSections = ShowroomFeaturedBrandsQuery['homepage']['sections'][0];
type HomePageFeaturedBrands = Extract<ShowroomFeaturedBrandsSections, { __typename?: 'HomePageFeaturedBrands' }>;

export const isShowroomFeaturedBrands = (args: ShowroomFeaturedBrandsSections): args is HomePageFeaturedBrands => {
	return args.__typename === 'HomePageFeaturedBrands' && args.items !== undefined;
};

export const countNumberOfShowrooms = (showrooms: ShowroomsQuery['showrooms']): number => {
	return showrooms.flatMap((showroom) => showroom.showroomsByCity.flatMap((city) => city.showrooms)).length;
};
